/**
 * <kahi-gallery>: a native scroll-snap image rail with thumbnails and dots.
 * Thumbnails scroll the rail; the active thumb and dot follow the visible slide.
 * Slides marked data-k-later are shown once the page has loaded, or as soon as the shopper
 * touches or focuses the gallery, so their images don't slow down the first one.
 */
class KahiGallery extends HTMLElement {
  #later;

  connectedCallback() {
    this.rail = this.querySelector('[data-k-rail]');
    this.slides = [...this.querySelectorAll('[data-k-slide]')];
    this.marks = [...this.querySelectorAll('[data-k-go]')];
    if (!this.rail || this.slides.length < 2) return;

    this.#later = new AbortController();
    if (document.readyState === 'complete') this.#showAll();
    else {
      const { signal } = this.#later;
      window.addEventListener('load', this.#showAll, { signal });
      this.addEventListener('pointerdown', this.#showAll, { signal });
      this.addEventListener('focusin', this.#showAll, { signal });
    }

    this.addEventListener('click', this.#onClick);
    this.observer = new IntersectionObserver(this.#onSeen, { root: this.rail, threshold: 0.6 });
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.#later?.abort();
    this.removeEventListener('click', this.#onClick);
    this.observer?.disconnect();
  }

  #showAll = () => {
    this.#later.abort();
    for (const slide of this.slides) slide.removeAttribute('data-k-later');
  };

  #onClick = (event) => {
    const mark = event.target.closest('[data-k-go]');
    if (!mark) return;
    this.#showAll();
    const slide = this.slides[Number(mark.dataset.kGo)];
    this.rail.scrollTo({ left: slide.offsetLeft - this.rail.offsetLeft, behavior: 'smooth' });
  };

  #onSeen = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = String(this.slides.indexOf(entry.target));
      for (const mark of this.marks) mark.toggleAttribute('aria-current', mark.dataset.kGo === index);
    }
  };
}

if (!customElements.get('kahi-gallery')) customElements.define('kahi-gallery', KahiGallery);
